import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/Auth";
import { fetchReviewerInbox } from "@/api/fetchReviewerInbox";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { FileText, CheckCircle2, Clock, LogOut, AlertCircle, Link2, File, CheckCircle, AlertTriangle, XCircle, FileWarning } from "lucide-react";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import { getLogoUrl } from "@/utils/getLogoUrl";
import csvProLogo from "@/assets/csv-pro-logo-new.png";

const sb = supabase as any;

type EvidenceSummary = {
  total_links: number;
  total_documents: number;
  total_count: number;
};

type ReviewerProgressSummary = {
  total_questions: number;
  questions_reviewed: number;
  implemented_count: number;
  partial_count: number;
  not_implemented_count: number;
  findings_count: number;
  high_risk_findings: number;
  medium_risk_findings: number;
  reviewer_progress_percent: number;
};

type InboxRow = {
  assessment_id: string;
  org_id: string;
  org_name: string;
  org_type: string | null;
  portfolio_logo: string | null;
  started_at: string | null;
  submitted_at: string | null;
  total_questions: number;
  answered_count: number;
  evidence_count: number;
  status?: string | null;
  created_at: string;
  reviewer_user_id: string;
  respondent_name: string | null;
  respondent_user_id: string | null;
};

type ComputedStatus = "Not Started" | "In Progress" | "Submitted" | "Closed";

function computeStatus(row: InboxRow): ComputedStatus {
  if (row.status === "Closed") return "Closed";
  if (row.submitted_at) return "Submitted";
  if (row.started_at) return "In Progress";
  return "Not Started";
}

export default function ReviewerInbox() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reviewerProgress, setReviewerProgress] = useState<Map<string, boolean>>(new Map());
  const [evidenceSummaries, setEvidenceSummaries] = useState<Map<string, EvidenceSummary>>(new Map());
  const [reviewerMetrics, setReviewerMetrics] = useState<Map<string, ReviewerProgressSummary>>(new Map());

  useEffect(() => {
    const loadInbox = async () => {
      if (!session?.user?.id) return;

      setLoading(true);
      setErr(null);

      try {
        const data = await fetchReviewerInbox(session.user.id);
        setRows(data);

        if (data.length > 0) {
          // Load progress, evidence summaries, and reviewer metrics in parallel
          const [progressResult, metricsResult, ...summaryResults] = await Promise.all([
            sb.rpc('reviewer_get_progress'),
            sb.rpc('reviewer_get_progress_summary'),
            ...data.map((row: InboxRow) =>
              sb.rpc('get_assessment_evidence_summary', { p_assessment_id: row.assessment_id })
                .then((res: any) => ({ id: row.assessment_id, data: res.data?.[0] ?? null }))
            )
          ]);

          // Reviewer progress (continue/begin logic)
          const progressMap = new Map();
          progressResult.data?.forEach((progress: any) => {
            progressMap.set(progress.assessment_id, true);
          });
          setReviewerProgress(progressMap);

          // Reviewer metrics
          const metricsMap = new Map<string, ReviewerProgressSummary>();
          metricsResult.data?.forEach((row: any) => {
            metricsMap.set(row.assessment_id, {
              total_questions: Number(row.total_questions) || 0,
              questions_reviewed: Number(row.questions_reviewed) || 0,
              implemented_count: Number(row.implemented_count) || 0,
              partial_count: Number(row.partial_count) || 0,
              not_implemented_count: Number(row.not_implemented_count) || 0,
              findings_count: Number(row.findings_count) || 0,
              high_risk_findings: Number(row.high_risk_findings) || 0,
              medium_risk_findings: Number(row.medium_risk_findings) || 0,
              reviewer_progress_percent: Number(row.reviewer_progress_percent) || 0,
            });
          });
          setReviewerMetrics(metricsMap);

          // Evidence summaries
          const summaryMap = new Map<string, EvidenceSummary>();
          summaryResults.forEach((r: any) => {
            if (r.data) {
              summaryMap.set(r.id, r.data);
            }
          });
          setEvidenceSummaries(summaryMap);
        }
      } catch (error: any) {
        setErr(error.message || "Failed to load reviewer inbox");
      }

      setLoading(false);
    };

    loadInbox();
  }, [session?.user?.id]);


  const statusBadge = (status: ComputedStatus) => {
    switch (status) {
      case "Submitted":
        return <Badge variant="secondary"><CheckCircle2 className="h-3 w-3 mr-1" />Submitted</Badge>;
      case "In Progress":
        return <Badge><Clock className="h-3 w-3 mr-1" />In Progress</Badge>;
      case "Not Started":
        return <Badge variant="outline"><AlertCircle className="h-3 w-3 mr-1" />Not Started</Badge>;
      case "Closed":
        return <Badge variant="secondary"><CheckCircle2 className="h-3 w-3 mr-1" />Closed</Badge>;
    }
  };

  const actionLabel = (assessmentId: string, status: ComputedStatus) => {
    if (status === "Closed") return "View Review";
    if (status === "Submitted") return "Open Review";
    const hasProgress = reviewerProgress.get(assessmentId);
    return hasProgress ? "Continue Review" : "Begin Review";
  };

  const openReview = (assessmentId: string) => {
    navigate(`/reviewer/${assessmentId}`);
  };

  const handleSignOut = async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      localStorage.removeItem("supabase.auth.token");
      localStorage.removeItem("cyber-assess-state");
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      navigate("/login");
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-4">
      {/* Header with CSV Pro logo */}
      <div className="flex justify-between items-center mb-4">
        <img src={csvProLogo} alt="CSV Pro" className="h-24 w-auto" />
        <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-muted-foreground hover:text-foreground">
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>

      <h1 className="text-xl font-bold">Assigned Assessments</h1>

      {err && (
        <Alert variant="destructive"><AlertDescription>Failed to load: {err}</AlertDescription></Alert>
      )}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-4"><Skeleton className="h-24 w-full" /></Card>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Alert><AlertDescription>No assigned assessments yet.</AlertDescription></Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((it) => {
            const status = computeStatus(it);
            const metrics = reviewerMetrics.get(it.assessment_id);
            const respondentPercent = it.total_questions
              ? Math.min(Math.round((it.answered_count / it.total_questions) * 100), 100)
              : 0;

            return (
              <Card key={it.assessment_id} className="hover:shadow-md transition">
                <CardHeader className="space-y-1">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <BrandedLogo
                        src={getLogoUrl(it.portfolio_logo)}
                        alt={it.org_name}
                        className="h-10 w-auto shrink-0"
                      />
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{it.org_name}</CardTitle>
                        {it.respondent_name && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Respondent: <span className="font-medium text-foreground/80">{it.respondent_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {statusBadge(status)}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 text-sm">
                  {/* Respondent Status Section */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Respondent Status</p>
                    <div className="flex justify-between">
                      <span>Respondent Started</span>
                      <span>{it.started_at ? new Date(it.started_at).toLocaleString() : "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Respondent Submitted</span>
                      <span>{it.submitted_at ? new Date(it.submitted_at).toLocaleString() : "—"}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Respondent Progress</span>
                      <div className="flex items-center gap-2">
                        <Progress value={respondentPercent} className="h-2 w-20" />
                        <span className="text-xs font-medium">{respondentPercent}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Respondent Evidence</span>
                      <span className="flex items-center gap-2">
                        {(() => {
                          const summary = evidenceSummaries.get(it.assessment_id);
                          if (!summary) return <span className="text-muted-foreground">—</span>;
                          return (
                            <>
                              <span className="inline-flex items-center gap-1">
                                <Link2 className="h-3 w-3 text-muted-foreground" />
                                {summary.total_links} Links
                              </span>
                              <span className="text-muted-foreground">|</span>
                              <span className="inline-flex items-center gap-1">
                                <File className="h-3 w-3 text-muted-foreground" />
                                {summary.total_documents} Documents
                              </span>
                            </>
                          );
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Reviewer Status Section */}
                  <div className="space-y-2 pt-3 border-t border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Reviewer Status</p>
                    {metrics ? (
                      <>
                        <div className="flex justify-between items-center">
                          <span>Reviewer Progress</span>
                          <div className="flex items-center gap-2">
                            <Progress value={Math.min(metrics.reviewer_progress_percent, 100)} className="h-2 w-20" />
                            <span className="text-xs font-medium">{Math.min(metrics.reviewer_progress_percent, 100)}%</span>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span>Questions Reviewed</span>
                          <span>{metrics.questions_reviewed} / {metrics.total_questions}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
                          <span className="inline-flex items-center gap-1 text-xs">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                            Implemented: {metrics.implemented_count}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs">
                            <AlertTriangle className="h-3 w-3 text-yellow-600" />
                            Partial: {metrics.partial_count}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs">
                            <XCircle className="h-3 w-3 text-red-600" />
                            Not Impl: {metrics.not_implemented_count}
                          </span>
                        </div>
                        {/* Findings breakdown */}
                        {metrics.findings_count > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1 border-t border-border mt-1">
                            <span className="inline-flex items-center gap-1 text-xs font-medium">
                              <FileWarning className="h-3 w-3 text-orange-600" />
                              Findings: {metrics.findings_count}
                            </span>
                            {metrics.high_risk_findings > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                                High Risk: {metrics.high_risk_findings}
                              </span>
                            )}
                            {metrics.medium_risk_findings > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-yellow-600 font-medium">
                                Medium Risk: {metrics.medium_risk_findings}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">Not started</p>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="justify-end">
                  <Button size="sm" onClick={() => openReview(it.assessment_id)}>
                    {actionLabel(it.assessment_id, status)}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
